import { GlobalStyles, useTheme } from '@mui/material';

export default function AppThemeGlobalStyles() {
  const theme = useTheme();
  const p = theme.palette;
  const isDark = p.mode === 'dark';

  const selectedBg =
    typeof p.background?.selected === 'string' && !p.background.selected.startsWith('var(')
      ? p.background.selected
      : '#5b9bd5';

  const br = typeof theme.shape?.borderRadius === 'number' ? theme.shape.borderRadius : 10;

  return (
    <GlobalStyles
      styles={{
        ':root': {
          '--toast-close-button-color': isDark ? '#f8fafc' : '#0f172a',
          '--background-default': p.background.default,
          '--background-hover': p.action?.hover ?? 'rgba(0,0,0,0.04)',
          '--text-primary': p.text.primary,
          // Existia só a primária, então folha de estilo que precisava de um
          // texto de apoio repetia a primária e perdia a hierarquia.
          '--text-secondary': p.text.secondary,
          '--table-header-light': isDark ? '#e2e8f0' : '#334155',
          '--table-header': isDark ? '#e2e8f0' : '#334155',
          '--app-surface': p.background.paper,
          '--app-muted-surface': p.background.secondary ?? p.background.default,
          '--app-border': p.divider,
          '--app-accent': p.primary.main,
          '--app-accent-hover': p.primary.dark ?? p.primary.main,
          '--app-room-selected': selectedBg,
          '--app-room-hover': isDark ? 'rgba(99, 172, 229, 0.35)' : 'rgba(99, 172, 229, 0.45)',
          '--app-message-sent': isDark ? '#2a3441' : '#e8eef4',
          '--app-message-sent-text': isDark ? '#cbd5e1' : '#475569',
          '--app-message-received': isDark ? '#3d6a94' : '#4b86b4',
          '--app-chat-panel': isDark ? '#151c24' : '#ffffff',
          '--app-input-area': isDark ? '#1e2730' : '#f1f4f8',
          '--app-input-border': isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(15, 23, 42, 0.12)',
          '--app-users-list': isDark ? '#1a2836' : '#1a3347',
          '--app-users-list-hover': isDark ? '#243a4f' : '#2a4d69',
          '--app-waiting-bg': p.primary.main,
          '--app-waiting-row-alt': isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.14)',
          '--app-waiting-hover': isDark ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.12)',
          '--app-history-row-even': isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.12)',
          '--app-history-row-odd': isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.06)',
          '--app-modal-header': p.primary.main,
          '--app-modal-content': p.background.paper,
          '--radius-sm': `${br - 2}px`,
          '--radius-md': `${br}px`,
          '--radius-lg': `${br + 4}px`,
          '--sidebar-shell-bg': '#2a4d69',
          '--quick-access-tab-inactive-bg': p.quickAccess?.tabInactiveBg ?? 'rgba(255, 255, 255, 0.12)',
          '--quick-access-tab-inactive-text': p.quickAccess?.tabInactiveText ?? 'rgba(255, 255, 255, 0.72)',
          '--quick-access-tab-active-bg': p.quickAccess?.tabActiveBg ?? 'rgba(255, 255, 255, 0.22)',
          '--quick-access-tab-active-text': p.quickAccess?.tabActiveText ?? 'rgba(255, 255, 255, 0.98)',
          '--quick-access-tab-active-accent': p.quickAccess?.tabActiveAccent ?? p.primary.main,
          '--app-sidebar-accent': isDark ? '#8bc4ed' : '#63a5c7',
          '--app-icon-muted': isDark ? 'rgba(241,245,249,0.45)' : 'rgba(15,23,42,0.45)',
          '--blue-400': '#0d7dd8',
          '--blue-500': isDark ? '#63ace5' : '#1e4d72',
          '--blue-600': '#63ace5',
          '--blue-700': '#2a4d69',
          '--blue-800': isDark ? '#2d3d4d' : '#4b86b4',
          '--blue-900': isDark ? '#121a22' : '#202c33',
        },
        body: {
          backgroundColor: p.background.default,
          color: p.text.primary,
          fontFamily: theme.typography.fontFamily,
          fontSize: '0.875rem',
        },
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: isDark ? 'rgba(255,255,255,0.2) transparent' : 'rgba(15,23,42,0.25) transparent',
        },
        '*::-webkit-scrollbar': {
          width: 10,
          height: 10,
        },
        '*::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(15, 23, 42, 0.22)',
          borderRadius: 20,
          border: '3px solid transparent',
          backgroundClip: 'padding-box',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: isDark ? 'rgba(255,255,255,0.28)' : 'rgba(15, 23, 42, 0.35)',
        },
        '.header': {
          fontSize: '1.35rem !important',
        },
        '.MuiSvgIcon-root': {
          fontSize: '1.1rem !important',
        },
        '.MuiButton-root': {
          minHeight: '34px !important',
          padding: '6px 14px !important',
          fontSize: '0.8125rem !important',
        },
        '.MuiIconButton-root': {
          padding: '6px !important',
        },
        '.MuiInputBase-root, .MuiOutlinedInput-root, .MuiFilledInput-root': {
          minHeight: '34px !important',
        },
        '.MuiInputBase-input, .MuiOutlinedInput-input, .MuiFilledInput-input': {
          fontSize: '0.8125rem !important',
          paddingTop: '7px !important',
          paddingBottom: '7px !important',
        },
        // Ícone nativo de seleção de data/hora (webkit): é um SVG escuro que some
        // no tema escuro. Invertendo, fica branco e visível. Cobre inputs
        // type=date/datetime-local/month/time em todo o app.
        'input::-webkit-calendar-picker-indicator': {
          filter: isDark ? 'invert(1)' : 'none',
          cursor: 'pointer',
          opacity: isDark ? 0.85 : 0.6,
        },
        '.MuiInputLabel-root': {
          fontSize: '0.78rem !important',
        },
        '.MuiTab-root': {
          minHeight: '36px !important',
          fontSize: '0.8125rem !important',
        },
        '.MuiChip-root': {
          height: '22px !important',
          fontSize: '0.72rem !important',
        },
        '.MuiFab-root': {
          width: '44px !important',
          height: '44px !important',
          minHeight: '44px !important',
        },
        '.left-sidebar': {
          padding: '10px 8px !important',
          gap: '0.45rem !important',
        },
        '.left-sidebar .sidebar-header': {
          marginBottom: '12px !important',
        },
        '.left-sidebar .nav-item': {
          minHeight: '30px !important',
          padding: '4px 0 !important',
        },
        '.left-sidebar .nav-label, .left-sidebar .theme-label, .left-sidebar .settings-title': {
          fontSize: '0.66rem !important',
        },
      }}
    />
  );
}
